import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { SortDirection } from '@Pages/RetroSynthesis/constants';

import './RsTableHeaderSortControl.scss';
import { Button, ButtonWithIcon } from '@els/els-react--button';

export function RsTableSortControlPure({ children, onChange = () => null, sortParam, sortList }) {
  const findSortState = (array, sortProperty) => {
    if (!array) {
      return null;
    }
    const { direction } = array.find((item) => item && item.property === sortProperty) || {};
    return direction;
  };

  const state = findSortState(sortList, sortParam);

  const getControlClickHandler = useCallback(
    (controlRule) => () => {
      if (state !== controlRule) {
        onChange(controlRule);
      }
    },
    [state, onChange]
  );

  return sortList && sortList.some((item) => item?.property === sortParam) ? (
    <ButtonWithIcon
      sprite={(state?.toLowerCase() || '') === 'asc' ? 'SortDesc' : 'SortAsc'}
      type="link"
      className="rs-table-header-sort__button"
      aria-label={(state?.toLowerCase() || '') === 'asc' ? 'Sort ascending' : 'Sort descending'}
      iconRight
      onClick={getControlClickHandler((state?.toLowerCase() || '') === 'asc' ? SortDirection.DESC : SortDirection.ASC)}>
      {children}
    </ButtonWithIcon>
  ) : (
    <div className="rs-table-header-sort c-els-table__sortable-button">
      <Button type="link" className="ul-els-margin-right-1o2" onClick={getControlClickHandler(SortDirection.DESC)}>
        {children}
      </Button>
      <div className="rs-table-header-sort__buttons">
        <ButtonWithIcon
          sprite="ChevronUp"
          linkIconSize="xs"
          iconOnly
          aria-label="Sort ascending"
          type="link"
          iconRight
          onClick={getControlClickHandler(SortDirection.DESC)}>
          {children}
        </ButtonWithIcon>
        <ButtonWithIcon
          sprite="ChevronDown"
          className="align-top"
          aria-label="Sort descending"
          linkIconSize="xs"
          iconOnly
          type="link"
          iconRight
          onClick={getControlClickHandler(SortDirection.ASC)}>
          {children}
        </ButtonWithIcon>
      </div>
    </div>
  );
}

RsTableSortControlPure.propTypes = {
  children: PropTypes.node,
  onChange: PropTypes.func,
  sortParam: PropTypes.string,
  sortList: PropTypes.arrayOf(
    PropTypes.shape({
      property: PropTypes.string,
      direction: PropTypes.oneOf(SortDirection.allTypes),
    })
  ),
};

// Wrapper around original SortControl to reduce some copy/paste
function RsTableHeaderSortControl({ sortList, sortParam, onChange = () => null, children }) {
  return (
    <RsTableSortControlPure
      sortList={sortList}
      sortParam={sortParam}
      onChange={(newValue) => onChange({ sort: `${sortParam},${newValue}` })}>
      {children}
    </RsTableSortControlPure>
  );
}

RsTableHeaderSortControl.propTypes = {
  sortList: PropTypes.arrayOf(
    PropTypes.shape({
      property: PropTypes.string,
      direction: PropTypes.oneOf(SortDirection.allTypes),
    })
  ),
  sortParam: PropTypes.string,
  onChange: PropTypes.func,
  children: PropTypes.node,
};

export default RsTableHeaderSortControl;
